import React, { useState, useEffect, useCallback } from "react";
import AthleteLayout from "./common/AthleteLayout";
import { AthleteHeader } from "./common/AthleteHeader";
import { useAthleteAuthContext } from "./login/useAthleteAuthContext";
import API_URL from "../api/API";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export const AthleteEditProfile = () => {
  const { athlete, dispatch } = useAthleteAuthContext();
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSaved, setIsSaved] = useState(false);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    phonenumber: "",
    email: "",
    password: "",
    training_preference: "",
    sport: "",
    sport_other: "",
    city: "",
    state: "",
    dob: "",
    gender: "",
    height_feet: "",
    height_inches: "",
    weight: "",
    pr_100m: "",
    pr_200m: "",
    pr_400m: "",
  });

  // ✅ Format date as MM-DD-YYYY
  const formatDateForStorage = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // ✅ Fetch athlete data
  // ✅ Fetch athlete data
  const fetchAthleteData = useCallback(async () => {
    if (athlete.email) {
      try {
        const response = await fetch(
          `${API_URL.GET_ATHLETE_BY_EMAIL}/${athlete.email}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${athlete.token}` },
          }
        );
        const data = await response.json();

        if (response.ok) {
          const formattedDOB = formatDateForStorage(data.dob);

          setFormData((prev) => ({
            ...prev,
            ...data,
            password: "",   // ← FORCE empty always
            dob: formattedDOB,
            sport:
              data.sport === "Other" && data.sport_other ? "Other" : data.sport,
            sport_other: data.sport_other || "",
            height_feet: data.height_feet || "",
            height_inches: data.height_inches || "",
          }));

          if (data.photo) {
            setPhotoPreview(API_URL.PROFILE_PICTURE_URL + data.photo);
            dispatch({ type: "UPDATE_ACCOUNT_PHOTO", payload: data.photo });
            const updatedAthlete = { ...athlete, photo: data.photo };
            localStorage.setItem("athlete", JSON.stringify(updatedAthlete));
          }
        } else {
          throw new Error(data.error || "Failed to fetch athlete data");
        }
      } catch (err) {
        setError(err.message);
      }
    }
  }, [athlete.email, athlete.token]);

  useEffect(() => {
    fetchAthleteData();
  }, [fetchAthleteData, isSaved]);

  // ✅ Handle change
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "sport") {
      setFormData((prev) => ({
        ...prev,
        sport: value,
        sport_other: value === "Other" ? prev.sport_other : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      dob: date ? date.toISOString() : null,
    }));
  };

  const handlePhotoChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // ✅ Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const formPayload = new FormData();

      // Append all fields except sport_other (we'll handle that manually)
      Object.keys(formData).forEach((key) => {
        if (key === "dob") {
          formPayload.append("dob", formatDateForStorage(formData.dob));
        } else if (key === "password") {
          if (formData.password.trim() !== "") {
            formPayload.append("password", formData.password);
          }
        } else if (key !== "sport_other") {
          formPayload.append(key, formData[key]);
        }
      });
      

      // ✅ Handle sport_other properly
      if (formData.sport === "Other") {
        formPayload.append("sport_other", formData.sport_other);
      } else {
        formPayload.append("sport_other", "");
      }

      if (photo) formPayload.append("photo", photo);

      const response = await fetch(
        `${API_URL.PATCH_ATHLETE_BY_EMAIL}/${athlete.email}`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${athlete.token}` },
          body: formPayload,
        }
      );

      const data = await response.json();
      setLoading(false);

      if (!response.ok) {
        setError(data.error || "Failed to update profile");
      } else {
        setSuccess("Profile updated successfully!");
        setIsSaved(true);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
    }
  };

  // ✅ Scroll on save
  useEffect(() => {
    if (isSaved) {
      setTimeout(() => {
        window.scroll({ top: 0, behavior: "smooth" });
        setIsSaved(false);
      }, 200);
    }
  }, [isSaved]);

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="w-full max-w-2xl mx-auto px-4 py-6">
            <h2 className="text-2xl mb-4">Edit Profile</h2>

            {error && <p className="text-red-500 mb-4">{error}</p>}
            {success && <p className="text-green-600 mb-4">{success}</p>}
            {loading && (
              <p className="text-yellow-500 mb-4">Processing, please wait...</p>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              <section className="bg-smock rounded-2xl p-4 space-y-4">
                <h3 className="text-lg font-semibold">Personal Information</h3>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">First Name</span>
                  <input
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Last Name</span>
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Phone Number</span>
                  <input
                    type="text"
                    name="phonenumber"
                    value={formData.phonenumber}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Email</span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    readOnly
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Password</span>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>
              </section>

              <section className="bg-smock rounded-2xl p-4 space-y-4">
                <h3 className="text-lg font-semibold">Training Preferences</h3>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">
                    Training Preference
                  </span>
                  <select
                    name="training_preference"
                    value={formData.training_preference}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  >
                    <option value="">Select</option>
                    <option value="on-site">ON-SITE TRAINING</option>
                    <option value="online">REMOTE TRAINING</option>
                  </select>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Sport</span>
                  <select
                    name="sport"
                    value={formData.sport}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  >
                    <option value="">Select</option>
                    <option value="Track and Field">Track and Field</option>
                    <option value="Football">Football</option>
                    <option value="Baseball">Baseball</option>
                    <option value="Softball">Softball</option>
                    <option value="Soccer">Soccer</option>
                    <option value="Other">Other</option>
                  </select>
                </label>

                {formData.sport === "Other" && (
                  <label className="flex flex-col gap-1">
                    <span className="text-sm">Other Sport</span>
                    <input
                      type="text"
                      name="sport_other"
                      value={formData.sport_other}
                      onChange={handleChange}
                      placeholder="Specify other sport"
                      className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                    />
                  </label>
                )}

                <label className="flex flex-col gap-1">
                  <span className="text-sm">City</span>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">State</span>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Date of Birth</span>
                  <DatePicker
                    selected={formData.dob ? new Date(formData.dob) : null}
                    onChange={handleDateChange}
                    dateFormat="MM-dd-yyyy"
                    showYearDropdown
                    dropdownMode="select"
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Gender</span>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </label>
              </section>

              <section className="bg-smock rounded-2xl p-4 space-y-4">
                <h3 className="text-lg font-semibold">Physical Stats</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 col-span-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm">Height (Feet)</span>
                    <input
                      type="number"
                      name="height_feet"
                      value={formData.height_feet}
                      onChange={handleChange}
                      className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm">
                      Height (Inches)
                    </span>
                    <input
                      type="number"
                      name="height_inches"
                      value={formData.height_inches}
                      onChange={handleChange}
                      className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                    />
                  </label>
                </div>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Weight (lbs)</span>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>
              </section>

              <section className="bg-smock rounded-2xl p-4 space-y-4">
                <h3 className="text-lg font-semibold">Performance Records</h3>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">PR 100m</span>
                  <input
                    type="text"
                    name="pr_100m"
                    value={formData.pr_100m}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">PR 200m</span>
                  <input
                    type="text"
                    name="pr_200m"
                    value={formData.pr_200m}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">PR 400m</span>
                  <input
                    type="text"
                    name="pr_400m"
                    value={formData.pr_400m}
                    onChange={handleChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm">Profile Photo</span>
                  <input
                    type="file"
                    onChange={handlePhotoChange}
                    className="border rounded-xl px-4 py-3 text-base font-light w-full focus:outline-none focus:ring-2 focus:ring-darkYellow"
                    accept="image/*"
                  />
                </label>

                {photoPreview && (
                  <div className="col-span-2">
                    <p className="text-sm mb-1 font-medium">Photo Preview:</p>
                    <img
                      src={photoPreview}
                      alt="Profile"
                      className="w-32 sm:w-40 md:w-48 rounded shadow-md border"
                    />
                  </div>
                )}
              </section>

              <div className="xs:pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="text-center w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300"
                >
                  {loading ? "Updating..." : "Update Profile"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};
